import { useMemo } from "react";
import { store, useAppSelector } from "../../store";
import { DatasourcesClient } from "../metastore-api-client";
import { ClientAppSettings } from "../providers/SettingsProvider";

export type PluginDataClient = {
    /**
     * POSTs the parameter values to a Dynamic Data API endpoint (e.g. "storyline-data/example/my_fn/my_fn",
     * or the same as segments) and resolves with the parsed JSON response.
     */
    fetchData: (
        endpoint: string | string[],
        parameterValues?: Record<string, unknown>,
        options?: { compressRequestBody?: boolean },
    ) => Promise<any>;
};

type DataSettings = Pick<ClientAppSettings, "metastoreUrl" | "dynamicDataApiUrl" | "maxDatasourceRetryCount" | "dynamicDataApiBypassesMetastore">;

const buildClient = (settings: DataSettings, activeBranch: string): PluginDataClient => {
    const client = new DatasourcesClient(settings.metastoreUrl, activeBranch, settings.maxDatasourceRetryCount, settings.dynamicDataApiBypassesMetastore);

    return {
        fetchData: (endpoint, parameterValues = {}, { compressRequestBody = true } = {}) =>
            client.fetchData(
                settings.dynamicDataApiUrl,
                Array.isArray(endpoint) ? endpoint.join("/") : endpoint,
                parameterValues,
                compressRequestBody,
            ),
    };
};

/**
 * A pre-wired DatasourcesClient for plugin pages: data URLs, branch and retry settings come from
 * the app state, and authentication is handled by the app's global fetch interceptor.
 */
export const useDatasourcesClient = (): PluginDataClient => {
    const settings = useAppSelector(state => state.app.settings);
    const activeBranch = useAppSelector(state => state.app.activeBranch);

    return useMemo(() => buildClient(settings, activeBranch), [settings, activeBranch]);
};

/** Non-hook variant for code outside the component tree; reads the live store state per call. */
export const createDatasourcesClient = (): PluginDataClient => {
    const { settings, activeBranch } = store.getState().app;
    return buildClient(settings, activeBranch);
};
